/**
 * Modelo compartido de los certificados digitales del usuario (SEC-04).
 *
 * `estado` es el estado visible de cada certificado; la fuente de verdad de
 * cuál es el activo es el documento `certificadosControl/{userId}`.
 */

export type EstadoCertificado = "preparado" | "activo" | "archivado";

/** Documento `certificadosControl/{userId}`: apunta al único certificado activo. */
export interface ControlCertificados {
  activoId: string;
  actualizadoEn: string;
}

/** Días que se conserva la clave privada de un certificado archivado. */
export const DIAS_CONSERVACION_CLAVE_ARCHIVADA = 30;

/** Resultado de comprobar un certificado nuevo contra FACe antes de activarlo. */
export type ResultadoComprobacionFace =
  | "aceptado"
  | "rechazado"
  | "error"
  | "no_verificable"
  | "omitida"
  | "forzada";

/** Última comprobación guardada en el certificado (`comprobacionFace`). */
export interface ComprobacionFace {
  resultado: ResultadoComprobacionFace;
  /** Entorno de FACe contra el que se comprobó; `null` si no se llegó a consultar. */
  entorno: "pruebas" | "produccion" | null;
  fecha: string;
  /** Código numérico de FACe (p. ej. "401"), si lo hubo; nunca texto de FACe. */
  codigo?: string;
  /** Frase fija propia de la aplicación; nunca el texto de FACe ni de una librería. */
  mensaje: string;
}

/**
 * Tareas posteriores a la renovación, solo en el certificado archivado: fecha
 * en que el usuario las marcó como hechas, o `null` si siguen pendientes.
 */
export interface TareasTrasRenovar {
  bajaFaceEn: string | null;
  revocacionFnmtEn: string | null;
}

/** Umbrales de los avisos de caducidad, en días restantes. */
export const DIAS_AVISO = {aviso: 60, importante: 30, urgente: 7} as const;

export type NivelCaducidad =
  "ok" | "aviso" | "importante" | "urgente" | "caducado";

export interface CaducidadCertificado {
  nivel: NivelCaducidad;
  diasRestantes: number;
}

const DIA_MS = 24 * 60 * 60 * 1000;

/**
 * Nivel del aviso según los días que faltan para la caducidad: `ok` (más de
 * 60), `aviso` (60 a 31), `importante` (30 a 8), `urgente` (7 a 0) y
 * `caducado` (negativo). `diasRestantes` son días completos (redondeo hacia
 * abajo): el instante exacto de caducidad cuenta como 0 y un milisegundo
 * después ya es -1. Una fecha inválida (de caducidad o `ahora`) se trata
 * como caducada.
 *
 * Es la única regla: la usan el servidor y el cliente.
 */
export function nivelCaducidad(
  validUntil: string | Date,
  ahora: Date = new Date()
): CaducidadCertificado {
  const caducidad = new Date(validUntil).getTime();
  const instanteActual = ahora.getTime();
  // Sin fecha de caducidad o sin reloj fiable, el lado seguro es «caducado»
  if (Number.isNaN(caducidad) || Number.isNaN(instanteActual)) {
    return {nivel: "caducado", diasRestantes: -1};
  }

  const diasRestantes = Math.floor((caducidad - instanteActual) / DIA_MS);
  if (diasRestantes < 0) return {nivel: "caducado", diasRestantes};
  if (diasRestantes <= DIAS_AVISO.urgente) return {nivel: "urgente", diasRestantes};
  if (diasRestantes <= DIAS_AVISO.importante) return {nivel: "importante", diasRestantes};
  if (diasRestantes <= DIAS_AVISO.aviso) return {nivel: "aviso", diasRestantes};
  return {nivel: "ok", diasRestantes};
}

/** Lo que los paneles y los avisos muestran del certificado activo. */
export interface ResumenCertificado {
  nombre: string;
  validUntil: string;
  diasRestantes: number;
  nivel: NivelCaducidad;
}

/** Portales de integradores de FACe, uno por entorno (el alta del certificado se hace ahí). */
export const PORTAL_INTEGRADORES_FACE = {
  produccion: "https://integradores.face.gob.es",
  pruebas: "https://se-integradores-face.redsara.es",
} as const;
